import { Direction } from '@angular/cdk/bidi';
import { Injectable } from '@angular/core';
import { MatLegacyDialog, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GetPerformanceAvailabilitiesRequest,
  GetPerformancesRequest,
} from '@grpc-clients/external/mercury/v1/entertainment/entertainment.pb';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TicketBookingDialogComponent } from '@pages/booking/components/ticket-booking-dialog/ticket-booking-dialog.component';
import { EntertainmentTicketDetailsService } from '@pages/entertainment/services/entertainment-ticket-details-page.service';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ENTERTAINMENT_TICKET_BOOK_TOKEN } from 'app.config';
import { catchError, finalize, map, of, switchMap, take, tap } from 'rxjs';
import {
  GET_TICKET_EVENT_DETAILS,
  GET_TICKET_EVENT_PERFORMANCES,
  GET_TICKET_EVENT_PERFORMANCES_AVAILABILITY,
  GET_TICKET_EVENT_PERFORMANCES_AVAILABILITY_FAILURE,
  GetTicketEventDetails,
  GetTicketEventDetailsFailure,
  GetTicketEventDetailsSuccess,
  GetTicketEventPerformanceAvailability,
  GetTicketEventPerformanceAvailabilityFailure,
  GetTicketEventPerformanceAvailabilitySuccess,
  GetTicketEventPerformances,
  GetTicketEventPerformancesFailure,
  GetTicketEventPerformancesSuccess,
  RESERVE_TICKETS,
  ReserveTickets,
  ReserveTicketsFailure,
  ReserveTicketsSuccess,
} from '../actions';

@Injectable()
export class EntertainmentDetailsSearchEffects {
  private dialogRef: MatLegacyDialogRef<TicketBookingDialogComponent, any>;

  constructor(
    private actions$: Actions,
    private ticketDetailsService: EntertainmentTicketDetailsService,
    private siteSettingsService: SiteSettingsService,
    private snackBar: MatSnackBar,
    private dialog: MatLegacyDialog
  ) {}

  public getTicketEventDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKET_EVENT_DETAILS),
      switchMap((action: GetTicketEventDetails) =>
        this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) => {
            return this.ticketDetailsService.getEventDetails(siteSettings.language, action.eventId).pipe(
              map((data) => new GetTicketEventDetailsSuccess(data)),
              catchError(() => of(new GetTicketEventDetailsFailure()))
            );
          })
        )
      )
    )
  );

  public geTicketEventPerformances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKET_EVENT_PERFORMANCES),
      switchMap((action: GetTicketEventPerformances) => {
        const request = new GetPerformancesRequest({
          eventId: action.payload.eventId,
          performanceId: action.payload.performanceId ? { value: action.payload.performanceId } : undefined,
          startDate: action.payload.startDate
            ? { value: action.payload.startDate ? action.payload.startDate.toLocaleString() : null }
            : undefined,
          endDate: action.payload.endDate
            ? { value: action.payload.endDate ? action.payload.endDate.toLocaleString() : null }
            : undefined,
        });

        return this.ticketDetailsService.getEventPerformance(request).pipe(
          map((data) => new GetTicketEventPerformancesSuccess(data)),
          catchError(() => of(new GetTicketEventPerformancesFailure()))
        );
      })
    )
  );

  public getTicketEventPerformanceAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TICKET_EVENT_PERFORMANCES_AVAILABILITY),
      switchMap((action: GetTicketEventPerformanceAvailability) =>
        this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) => {
            const request = new GetPerformanceAvailabilitiesRequest({
              currencyCode: siteSettings.currency,
              languageCode: siteSettings.language,
              performanceIds: action.performanceIds,
            });
            return this.ticketDetailsService.getEventPerformanceAvailability(request).pipe(
              map((data) => new GetTicketEventPerformanceAvailabilitySuccess(data)),
              catchError(() => of(new GetTicketEventPerformanceAvailabilityFailure()))
            );
          })
        )
      )
    )
  );

  public reserveTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RESERVE_TICKETS),
      switchMap((action: ReserveTickets) => {
        return this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) => {
            this.dialogRef = this.showTicketBookingDialog();
            return this.ticketDetailsService
              .reserveTicket(siteSettings.language, siteSettings.currency, action.payload)
              .pipe(
                tap((response) =>
                  window.localStorage.setItem(ENTERTAINMENT_TICKET_BOOK_TOKEN, response.data.bookToken)
                ),
                map((data) => new ReserveTicketsSuccess(data.data, data.paymentToken, action.payload.performanceId)),
                catchError(() => of(new ReserveTicketsFailure())),
                finalize(() => this.dialogRef.close())
              );
          })
        );
      })
    )
  );

  public error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GET_TICKET_EVENT_PERFORMANCES_AVAILABILITY_FAILURE),
        tap(() => {
          this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 5000 });
        })
      ),
    { dispatch: false }
  );

  private showTicketBookingDialog(): MatLegacyDialogRef<TicketBookingDialogComponent> {
    return this.dialog.open(TicketBookingDialogComponent, {
      direction: document.querySelector('html')?.dir as Direction,
      disableClose: true,
      data: 'Entertainment:ReserveTickets',
      backdropClass: 'ticket-booking-dialog-backdrop',
      panelClass: 'ticket-booking-dialog',
    });
  }
}
